import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_sentryserver_485e2b94 from 'nuxt_plugin_sentryserver_485e2b94' // Source: ./sentry.server.js (mode: 'server')
import nuxt_plugin_sentryclient_761778ae from 'nuxt_plugin_sentryclient_761778ae' // Source: ./sentry.client.js (mode: 'client')
import nuxt_plugin_bootstrapvue_10f808a1 from 'nuxt_plugin_bootstrapvue_10f808a1' // Source: ./bootstrap-vue.js (mode: 'all')
import nuxt_plugin_recaptcha_a6927c3e from 'nuxt_plugin_recaptcha_a6927c3e' // Source: ./recaptcha.js (mode: 'all')
import nuxt_plugin_dayjsplugin_3994cfaa from 'nuxt_plugin_dayjsplugin_3994cfaa' // Source: ./dayjs-plugin.js (mode: 'all')
import nuxt_plugin_pluginutils_3a8ae409 from 'nuxt_plugin_pluginutils_3a8ae409' // Source: ./nuxt-i18n/plugin.utils.js (mode: 'all')
import nuxt_plugin_pluginrouting_79629314 from 'nuxt_plugin_pluginrouting_79629314' // Source: ./nuxt-i18n/plugin.routing.js (mode: 'all')
import nuxt_plugin_pluginmain_29c150db from 'nuxt_plugin_pluginmain_29c150db' // Source: ./nuxt-i18n/plugin.main.js (mode: 'all')
import nuxt_plugin_workbox_02b4dd0e from 'nuxt_plugin_workbox_02b4dd0e' // Source: ./workbox.js (mode: 'client')
import nuxt_plugin_metaplugin_00c193be from 'nuxt_plugin_metaplugin_00c193be' // Source: ./pwa/meta.plugin.js (mode: 'all')
import nuxt_plugin_iconplugin_0446db95 from 'nuxt_plugin_iconplugin_0446db95' // Source: ./pwa/icon.plugin.js (mode: 'all')
import nuxt_plugin_axios_db3c871c from 'nuxt_plugin_axios_db3c871c' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_gtm_dfaa1070 from 'nuxt_plugin_gtm_dfaa1070' // Source: ./gtm.js (mode: 'all')
import nuxt_plugin_image_7d68b96d from 'nuxt_plugin_image_7d68b96d' // Source: ./image.js (mode: 'all')
import nuxt_plugin_vuexpersistedcookie_97cddd16 from 'nuxt_plugin_vuexpersistedcookie_97cddd16' // Source: ../plugins/vuex-persisted-cookie.js (mode: 'all')
import nuxt_plugin_get_658be510 from 'nuxt_plugin_get_658be510' // Source: ../plugins/get.js (mode: 'all')
import nuxt_plugin_toast_bc840172 from 'nuxt_plugin_toast_bc840172' // Source: ../plugins/toast.js (mode: 'all')
import nuxt_plugin_route_17b85036 from 'nuxt_plugin_route_17b85036' // Source: ../plugins/route.js (mode: 'all')
import nuxt_plugin_vuemasonry_087bf870 from 'nuxt_plugin_vuemasonry_087bf870' // Source: ../plugins/vue-masonry (mode: 'client')
import nuxt_plugin_googleanalytics_eaded2e4 from 'nuxt_plugin_googleanalytics_eaded2e4' // Source: ../plugins/google-analytics.js (mode: 'client')
import nuxt_plugin_persistedstate_125f8826 from 'nuxt_plugin_persistedstate_125f8826' // Source: ../checkoutForm/plugins/persisted-state.js (mode: 'client')
import nuxt_plugin_vuefinalmodal_4c991ad5 from 'nuxt_plugin_vuefinalmodal_4c991ad5' // Source: ../checkoutForm/plugins/vue-final-modal.js (mode: 'client')
import nuxt_plugin_vuenotification_4733c910 from 'nuxt_plugin_vuenotification_4733c910' // Source: ../checkoutForm/plugins/vue-notification.js (mode: 'client')
import nuxt_plugin_vuelidate_54ca6373 from 'nuxt_plugin_vuelidate_54ca6373' // Source: ../checkoutForm/plugins/vuelidate.js (mode: 'all')
import nuxt_plugin_storeInit_298298cc from 'nuxt_plugin_storeInit_298298cc' // Source: ../plugins/storeInit (mode: 'all')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root.$options.$nuxt
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext, config)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"title":"Found.us","meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1"},{"hid":"description","name":"description","content":""},{"hid":"og-type","property":"og:type","content":"website"},{"hid":"twitter:card","property":"twitter:card","content":"summary_large_image"},{"hid":"og:image:alt","property":"og:image:alt","content":"Preview image"},{"hid":"og:image","property":"og:image","content":".\u002Flogo.svg"},{"hid":"twitter:image","name":"twitter:image","content":".\u002Flogo.svg"},{"name":"format-detection","content":"telephone=no"}],"link":[{"rel":"icon","type":"image\u002Fx-icon","href":"\u002Ffavicon.ico"}],"script":[{"src":"https:\u002F\u002Fwww.googleoptimize.com\u002Foptimize.js?id=OPT-M2TVPG3"},{"hid":"gtm-script","innerHTML":"if(!window._gtm_init){window._gtm_init=1;(function(w,n,d,m,e,p){w[d]=(w[d]==1||n[d]=='yes'||n[d]==1||n[m]==1||(w[e]&&w[e][p]&&w[e][p]()))?1:0})(window,navigator,'doNotTrack','msDoNotTrack','external','msTrackingProtectionEnabled');(function(w,d,s,l,x,y){w[x]={};w._gtm_inject=function(i){if(w.doNotTrack||w[x][i])return;w[x][i]=1;w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s);j.defer=true;j.src='https:\u002F\u002Fwww.googletagmanager.com\u002Fgtm.js?id='+i;f.parentNode.insertBefore(j,f);};w[y]('GTM-W9DVJSCZ')})(window,document,'script','dataLayer','_gtm_ids','_gtm_inject')}"}],"style":[],"noscript":[{"hid":"gtm-noscript","pbody":true,"innerHTML":"\u003Ciframe src=\"https:\u002F\u002Fwww.googletagmanager.com\u002Fns.html?id=GTM-W9DVJSCZ&\" height=\"0\" width=\"0\" style=\"display:none;visibility:hidden\" title=\"gtm\"\u003E\u003C\u002Fiframe\u003E"}],"__dangerouslyDisableSanitizersByTagID":{"gtm-script":["innerHTML"],"gtm-noscript":["innerHTML"]}},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (process.server && typeof nuxt_plugin_sentryserver_485e2b94 === 'function') {
    await nuxt_plugin_sentryserver_485e2b94(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryclient_761778ae === 'function') {
    await nuxt_plugin_sentryclient_761778ae(app.context, inject)
  }

  if (typeof nuxt_plugin_bootstrapvue_10f808a1 === 'function') {
    await nuxt_plugin_bootstrapvue_10f808a1(app.context, inject)
  }

  if (typeof nuxt_plugin_recaptcha_a6927c3e === 'function') {
    await nuxt_plugin_recaptcha_a6927c3e(app.context, inject)
  }

  if (typeof nuxt_plugin_dayjsplugin_3994cfaa === 'function') {
    await nuxt_plugin_dayjsplugin_3994cfaa(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginutils_3a8ae409 === 'function') {
    await nuxt_plugin_pluginutils_3a8ae409(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginrouting_79629314 === 'function') {
    await nuxt_plugin_pluginrouting_79629314(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginmain_29c150db === 'function') {
    await nuxt_plugin_pluginmain_29c150db(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_workbox_02b4dd0e === 'function') {
    await nuxt_plugin_workbox_02b4dd0e(app.context, inject)
  }

  if (typeof nuxt_plugin_metaplugin_00c193be === 'function') {
    await nuxt_plugin_metaplugin_00c193be(app.context, inject)
  }

  if (typeof nuxt_plugin_iconplugin_0446db95 === 'function') {
    await nuxt_plugin_iconplugin_0446db95(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_db3c871c === 'function') {
    await nuxt_plugin_axios_db3c871c(app.context, inject)
  }

  if (typeof nuxt_plugin_gtm_dfaa1070 === 'function') {
    await nuxt_plugin_gtm_dfaa1070(app.context, inject)
  }

  if (typeof nuxt_plugin_image_7d68b96d === 'function') {
    await nuxt_plugin_image_7d68b96d(app.context, inject)
  }

  if (typeof nuxt_plugin_vuexpersistedcookie_97cddd16 === 'function') {
    await nuxt_plugin_vuexpersistedcookie_97cddd16(app.context, inject)
  }

  if (typeof nuxt_plugin_get_658be510 === 'function') {
    await nuxt_plugin_get_658be510(app.context, inject)
  }

  if (typeof nuxt_plugin_toast_bc840172 === 'function') {
    await nuxt_plugin_toast_bc840172(app.context, inject)
  }

  if (typeof nuxt_plugin_route_17b85036 === 'function') {
    await nuxt_plugin_route_17b85036(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuemasonry_087bf870 === 'function') {
    await nuxt_plugin_vuemasonry_087bf870(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_googleanalytics_eaded2e4 === 'function') {
    await nuxt_plugin_googleanalytics_eaded2e4(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_persistedstate_125f8826 === 'function') {
    await nuxt_plugin_persistedstate_125f8826(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuefinalmodal_4c991ad5 === 'function') {
    await nuxt_plugin_vuefinalmodal_4c991ad5(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuenotification_4733c910 === 'function') {
    await nuxt_plugin_vuenotification_4733c910(app.context, inject)
  }

  if (typeof nuxt_plugin_vuelidate_54ca6373 === 'function') {
    await nuxt_plugin_vuelidate_54ca6373(app.context, inject)
  }

  if (typeof nuxt_plugin_storeInit_298298cc === 'function') {
    await nuxt_plugin_storeInit_298298cc(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
